import { useQuery } from '@tanstack/react-query';
import styled from '@emotion/styled';
import { useAppSelector } from 'store';
import {
  selectIsDropshipSetup,
  selectIsGetStartedComplete,
  selectIsBuyer,
} from 'store/selectors/me/company';
import Menu from 'storybookConfig/stories/cells/Menu';
import Chip from 'storybookConfig/stories/molecules/Chip';
import { getPartners } from 'utils/api/partners';
import MarketPlaceLinks from './MarketplaceLinks';
import DropshipLinks from './DropshipLinks';
import useFlagEnabled from 'hooks/useFlagEnabled';

const NavBetaChip = styled(Chip)`
  color: inherit;
  border-color: currentColor;
  height: 14px;
  padding: 0 4px;
  font-size: 10px;
`;

const FETCHING_PENDING_PARTNERS_QUERY_KEY = ['getPartners', { pending: true }] as const;

interface MainMenuProps {
  isActive: (path: string) => boolean;
}

const MainMenu = ({ isActive }: MainMenuProps) => {
  const isGetStartedComplete = useAppSelector(selectIsGetStartedComplete);
  const isDropshipSetup = useAppSelector(selectIsDropshipSetup);
  const isBuyer = useAppSelector(selectIsBuyer);
  const showMarketplace = useFlagEnabled('showMarketplace');

  /**
   * Queries
   */

  const fetchingPendingPartners = useQuery({
    queryKey: FETCHING_PENDING_PARTNERS_QUERY_KEY,
    queryFn: ({ queryKey }) => getPartners(queryKey[1]),
    enabled: isDropshipSetup,
  });

  /**
   * Helpers
   */

  const partnerCount = fetchingPendingPartners.data?.data?.length;

  /**
   * Render Guards
   */

  if (!isDropshipSetup) {
    return (
      <Menu>
        <Menu.Link
          to="/onboarding"
          $iconName="select_check_box"
          $isActive={isActive('/onboarding')}
        >
          Set Up Dropship
        </Menu.Link>
      </Menu>
    );
  }

  /**
   * Render
   */

  return (
    <Menu>
      {!isGetStartedComplete && (
        <Menu.Link
          to="/get-started"
          $iconName="select_check_box"
          $isActive={isActive('/get-started')}
        >
          Get Started
        </Menu.Link>
      )}

      {isBuyer && (
        <Menu.Link
          to="/marketplace/discover"
          $iconName="travel_explore"
          $isActive={isActive('/marketplace/discover')}
        >
          Discover
          <NavBetaChip
            variant={isActive('/marketplace/discover') ? 'default' : 'light'}
            size="small"
          >
            BETA
          </NavBetaChip>
        </Menu.Link>
      )}

      {showMarketplace && <MarketPlaceLinks isActive={isActive} />}

      <DropshipLinks isActive={isActive} partnerCount={partnerCount || 0} />
    </Menu>
  );
};

export default MainMenu;
